import { AssetTypes, FamilyRelationshipTypes, FinancialInstitutionTypes } from "@nestoca/multi-tenant";

import {
  NonTraditionalGiftRelationshipEnum,
  EquityGiftRelationshipEnum,
  CurrencyEnum,
  VehicleType,
} from "./asset.enum";
import { AssetType, FamilyRelationshipType, FinancialInstitutionType } from "./asset.types";

export const MAX_VALUE = 10_000_000;
export const RRSP_NON_FTB_RATIO = 0.7;

export const financialTypes = [
  AssetTypes.SAVINGS,
  AssetTypes.TFSA,
  AssetTypes.RRSP,
  AssetTypes.NON_REGISTERED_INVESTMENTS,
  AssetTypes.HELOC,
  AssetTypes.CHEQUING,
] as const;

export const giftTypes = [
  AssetTypes.GIFT,
  AssetTypes.NON_TRADITIONAL_GIFT,
  AssetTypes.EQUITY_GIFT,
] as const;

// basic asset types only require description and amount used for down payment
export const basicAssetTypes = [
  AssetTypes.BORROWED_AGAINST_PROVEN_ASSETS,
  AssetTypes.BORROWED_DOWN_PAYMENT,
  AssetTypes.COMMERCIAL_ACCOUNT,
  AssetTypes.DEPOSIT_ON_PURCHASE,
  AssetTypes.EXISTING_EQUITY,
  AssetTypes.GRANTS,
  AssetTypes.LAND,
  AssetTypes.SECONDARY_FINANCING,
  AssetTypes.FUND_STOCK_BOND,
  AssetTypes.TERM_DEPOSIT,
  AssetTypes.SALE_OF_PURCHASE,
  AssetTypes.UNSECURED_CREDIT,
  AssetTypes.PROCEEDS_FROM_BUSINESS,
  AssetTypes.PROCEEDS_FROM_SALE_OF_BUSINESS,
  AssetTypes.AFFORDABLE_HOUSING_PROGRAM,
  AssetTypes.FIRST_TIME_HOME_BUYER_INCENTIVE,
  AssetTypes.GOVERNMENT_BENEFIT,
  AssetTypes.INHERITANCE,
  AssetTypes.LEGAL_INSURANCE_SETTLEMENT,
  AssetTypes.LOTTERY_CASINO_WINNINGS,
  AssetTypes.TRUST_FUNDS,
  AssetTypes.SINGLE_BUILDER_ADVANCE,
  AssetTypes.WIRE_PAYMENTS,
  AssetTypes.SWEAT_EQUITY,
] as const;

export const financialInstitutions = [
  FinancialInstitutionTypes.RBC,
  FinancialInstitutionTypes.TD,
  FinancialInstitutionTypes.SCOTIA_BANK,
  FinancialInstitutionTypes.BMO,
  FinancialInstitutionTypes.CIBC,
  FinancialInstitutionTypes.NBC,
  FinancialInstitutionTypes.DESJARDINS,
  FinancialInstitutionTypes.SUNLIFE,
  FinancialInstitutionTypes.HSBC,
  FinancialInstitutionTypes.TANGERINE,
  FinancialInstitutionTypes.IG,
  FinancialInstitutionTypes.QUESTTRADE,
  FinancialInstitutionTypes.WEALTH_SIMPLE,
  FinancialInstitutionTypes.ETRADE,
  FinancialInstitutionTypes.EQB,
  FinancialInstitutionTypes.ITRADE,
  FinancialInstitutionTypes.QTRADE_INVESTOR,
  FinancialInstitutionTypes.LAURENTIAN,
] as const;

export const ASSET_TYPES_LABEL: Record<AssetType, string> = {
  [AssetTypes.SAVINGS]: "form:assetType.savings",
  [AssetTypes.TFSA]: "form:assetType.tfsa",
  [AssetTypes.RRSP]: "form:assetType.rrsp",
  [AssetTypes.NON_REGISTERED_INVESTMENTS]:
    "form:assetType.nonRegisteredInvestments",
  [AssetTypes.GIFT]: "form:assetType.gift",
  [AssetTypes.VEHICLE]: "form:assetType.vehicle",
  [AssetTypes.PROPERTY]: "form:assetType.property",
  [AssetTypes.OTHER]: "form:assetType.other",
  [AssetTypes.NON_TRADITIONAL_GIFT]: "form:assetType.nonTraditionalGift",
  [AssetTypes.EQUITY_GIFT]: "form:assetType.equityGift",
  [AssetTypes.LAND]: "form:assetType.land",
  [AssetTypes.HELOC]: "form:assetType.heloc",
  [AssetTypes.SECONDARY_FINANCING]: "form:assetType.secondaryFinancing",
  [AssetTypes.COMMERCIAL_ACCOUNT]: "form:assetType.commercialAccount",
  [AssetTypes.DEPOSIT_ON_PURCHASE]: "form:assetType.depositOnPurchase",
  [AssetTypes.GRANTS]: "form:assetType.grants",
  [AssetTypes.BORROWED_AGAINST_PROVEN_ASSETS]:
    "form:assetType.borrowedAgainstProvenAssets",
  [AssetTypes.BORROWED_DOWN_PAYMENT]: "form:assetType.borrowedDownPayment",
  [AssetTypes.EXISTING_EQUITY]: "form:assetType.existingEquity",
  [AssetTypes.CHEQUING]: "form:assetType.chequing",
  [AssetTypes.FUND_STOCK_BOND]: "form:assetType.fundStockBond",
  [AssetTypes.TERM_DEPOSIT]: "form:assetType.termDeposit",
  [AssetTypes.SALE_OF_PURCHASE]: "form:assetType.saleOfPurchase",
  [AssetTypes.UNSECURED_CREDIT]: "form:assetType.unsecuredCredit",
  [AssetTypes.PROCEEDS_FROM_BUSINESS]: "form:assetType.proceedsBusiness",
  [AssetTypes.PROCEEDS_FROM_SALE_OF_BUSINESS]: "form:assetType.proceedsSaleBusiness",
  [AssetTypes.AFFORDABLE_HOUSING_PROGRAM]: "form:assetType.affordableHousingProgram",
  [AssetTypes.FIRST_TIME_HOME_BUYER_INCENTIVE]: "form:assetType.firstTimeOwnerIncentive",
  [AssetTypes.GOVERNMENT_BENEFIT]: "form:assetType.govBenefit",
  [AssetTypes.INHERITANCE]: "form:assetType.inheritance",
  [AssetTypes.LEGAL_INSURANCE_SETTLEMENT]: "form:assetType.legalInsuranceSettlement",
  [AssetTypes.LOTTERY_CASINO_WINNINGS]: "form:assetType.lotteryCasinoWinnings",
  [AssetTypes.TRUST_FUNDS]: "form:assetType.trustFunds",
  [AssetTypes.SINGLE_BUILDER_ADVANCE]: "form:assetType.singleBuilderAdvance",
  [AssetTypes.WIRE_PAYMENTS]: "form:assetType.wirePayments",
  [AssetTypes.SWEAT_EQUITY]: "form:assetType.sweatEquity",
};

export const RELATIONSHIP_TYPES_LABEL: Record<FamilyRelationshipType, string> = {
  [FamilyRelationshipTypes.FATHER]: "form:familyRelationship.father",
  [FamilyRelationshipTypes.MOTHER]: "form:familyRelationship.mother",
  [FamilyRelationshipTypes.CHILD]: "form:familyRelationship.child",
  [FamilyRelationshipTypes.BROTHER]: "form:familyRelationship.brother",
  [FamilyRelationshipTypes.SISTER]: "form:familyRelationship.sister",
  [FamilyRelationshipTypes.GRAND_FATHER]: "form:familyRelationship.grandFather",
  [FamilyRelationshipTypes.GRAND_MOTHER]: "form:familyRelationship.grandMother",
  [FamilyRelationshipTypes.UNCLE]: "form:familyRelationship.uncle",
  [FamilyRelationshipTypes.AUNT]: "form:familyRelationship.aunt",
  [FamilyRelationshipTypes.COUSIN]: "form:familyRelationship.cousin",
  [FamilyRelationshipTypes.FRIEND]: "form:familyRelationship.friend",
  [FamilyRelationshipTypes.OTHER]: "form:familyRelationship.other",
  [FamilyRelationshipTypes.PARENT]: "form:familyRelationship.parent",
  [FamilyRelationshipTypes.SIBLING]: "form:familyRelationship.sibling",
  [FamilyRelationshipTypes.GRANDPARENT]: "form:familyRelationship.grandParent",
  [FamilyRelationshipTypes.SPOUSE]: "form:familyRelationship.spouse",
};

export const NON_TRADITIONAL_GIFT_RELATIONSHIP_OPTIONS = [
  {
    label: "form:familyRelationship.uncle",
    value: NonTraditionalGiftRelationshipEnum.UNCLE,
  },
  {
    label: "form:familyRelationship.aunt",
    value: NonTraditionalGiftRelationshipEnum.AUNT,
  },
  {
    label: "form:familyRelationship.cousin",
    value: NonTraditionalGiftRelationshipEnum.COUSIN,
  },
  {
    label: "form:familyRelationship.friend",
    value: NonTraditionalGiftRelationshipEnum.FRIEND,
  },
];

export const EQUITY_GIFT_RELATIONSHIP_OPTIONS = [
  {
    label: "form:familyRelationship.father",
    value: EquityGiftRelationshipEnum.FATHER,
  },
  {
    label: "form:familyRelationship.mother",
    value: EquityGiftRelationshipEnum.MOTHER,
  },
  {
    label: "form:familyRelationship.child",
    value: EquityGiftRelationshipEnum.CHILD,
  },
  {
    label: "form:familyRelationship.brother",
    value: EquityGiftRelationshipEnum.BROTHER,
  },
  {
    label: "form:familyRelationship.sister",
    value: EquityGiftRelationshipEnum.SISTER,
  },
  {
    label: "form:familyRelationship.grandFather",
    value: EquityGiftRelationshipEnum.GRAND_FATHER,
  },
  {
    label: "form:familyRelationship.grandMother",
    value: EquityGiftRelationshipEnum.GRAND_MOTHER,
  },
];

export const FINANCIAL_INSTITUTION_LABELS: Record<
  FinancialInstitutionType,
  string
> = {
  [FinancialInstitutionTypes.BMO]: "applications:financialInstitution.bmo",
  [FinancialInstitutionTypes.CIBC]: "applications:financialInstitution.cibc",
  [FinancialInstitutionTypes.DESJARDINS]:
    "applications:financialInstitution.desjardins",
  [FinancialInstitutionTypes.EQB]: "applications:financialInstitution.eqb",
  [FinancialInstitutionTypes.ETRADE]: "applications:financialInstitution.etrade",
  [FinancialInstitutionTypes.HSBC]: "applications:financialInstitution.hsbc",
  [FinancialInstitutionTypes.IG]: "applications:financialInstitution.ig",
  [FinancialInstitutionTypes.ITRADE]: "applications:financialInstitution.itrade",
  [FinancialInstitutionTypes.LAURENTIAN]:
    "applications:financialInstitution.laurentian",
  [FinancialInstitutionTypes.NBC]: "applications:financialInstitution.nbc",
  [FinancialInstitutionTypes.QTRADE_INVESTOR]:
    "applications:financialInstitution.qtrade_investor",
  [FinancialInstitutionTypes.QUESTTRADE]:
    "applications:financialInstitution.questtrade",
  [FinancialInstitutionTypes.RBC]: "applications:financialInstitution.rbc",
  [FinancialInstitutionTypes.SCOTIA_BANK]:
    "applications:financialInstitution.scotia_bank",
  [FinancialInstitutionTypes.SUNLIFE]:
    "applications:financialInstitution.sunlife",
  [FinancialInstitutionTypes.TANGERINE]:
    "applications:financialInstitution.tangerine",
  [FinancialInstitutionTypes.TD]: "applications:financialInstitution.td",
  [FinancialInstitutionTypes.WEALTH_SIMPLE]:
    "applications:financialInstitution.wealth_simple",
  [FinancialInstitutionTypes.OTHER]: "applications:financialInstitution.other",
  [FinancialInstitutionTypes.ALBERTA_TREASURY_BRANCHES]: "applications:financialInstitution.abt",
  [FinancialInstitutionTypes.CAISSE_POPULAIRE_CREDIT_UNIONS]: "applications:financialInstitution.creditUnions",
  [FinancialInstitutionTypes.CITIZENS_BANK]: "applications:financialInstitution.citizensBank",
  [FinancialInstitutionTypes.DUNDEE_BANK_OF_CANADA]: "applications:financialInstitution.dundeeBankOfCanada",
  [FinancialInstitutionTypes.FIRSTLINE_MORTGAGES]: "applications:financialInstitution.firstlineMortgages",
  [FinancialInstitutionTypes.HOME_EQUITY_BANK]: "applications:financialInstitution.homeEquityBank",
  [FinancialInstitutionTypes.ICICI_BANK_CANADA]: "applications:financialInstitution.iciciBankCanada",
  [FinancialInstitutionTypes.INDUSTRIAL_ALLIANCE]: "applications:financialInstitution.industrialAlliance",
  [FinancialInstitutionTypes.MANULIFE_BANK_FINANCIAL]: "applications:financialInstitution.manulife",
  [FinancialInstitutionTypes.MCAP_MORTGAGE_CORP]: "applications:financialInstitution.mcapMortgageCorp",
  [FinancialInstitutionTypes.PRESIDENTS_CHOICE_FINANCIAL]: "applications:financialInstitution.pcFinancial",
  [FinancialInstitutionTypes.COASTAL_CAPITAL]: "applications:financialInstitution.coastalCaptial",
  [FinancialInstitutionTypes.PRIVATE]: "applications:financialInstitution.private",
  [FinancialInstitutionTypes.VANCITY]: "applications:financialInstitution.vancity",
};

export const CURRENCY_OPTIONS = [
  {
    value: CurrencyEnum.CAD,
    label: "form:asset.currency.cad"
  },
  {
    value: CurrencyEnum.USD,
    label: "form:asset.currency.usd"
  }
];

export const VEHICLE_TYPE_OPTIONS = [
  {
    value: VehicleType.SEDAN,
    label: "form:asset.vehicleType.sedan"
  },
  {
    value: VehicleType.SUV,
    label: "form:asset.vehicleType.suv"
  },
  {
    value: VehicleType.TRUCK,
    label: "form:asset.vehicleType.truck"
  }
];
